export interface IProjectTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  suggestedName: string;
  boards: {
    name: string;
    columns: {
      title: string;
      color: string;
      tasks: string[];
    }[];
  }[];
  docs: {
    name: string; // Doc container name (e.g., "Requirements")
    items: {
      title: string;
      content: string; // Markdown-style content that will be converted
    }[];
  }[];
}

export const projectTemplates: IProjectTemplate[] = [
  {
    id: 'blank',
    name: 'Blank Project',
    description: 'Start fresh with an empty project',
    icon: 'add',
    color: '#64748B',
    suggestedName: '',
    boards: [],
    docs: [],
  },
  {
    id: 'solopreneur',
    name: 'Solopreneur',
    description: 'Perfect for freelancers and independent creators managing their own work',
    icon: 'person',
    color: '#8B5CF6',
    suggestedName: 'My Business',
    boards: [
      {
        name: 'Task Board',
        columns: [
          {
            title: 'Backlog',
            color: '#F1F5F9',
            tasks: [
              'Research competitor pricing',
              'Update portfolio website',
              'Create invoice template',
            ],
          },
          {
            title: 'This Week',
            color: '#DBEAFE',
            tasks: [
              'Send proposal to new client',
              'Review project requirements',
            ],
          },
          {
            title: 'In Progress',
            color: '#FEF3C7',
            tasks: ['Design landing page mockup'],
          },
          {
            title: 'Done',
            color: '#D1FAE5',
            tasks: ['Set up business email'],
          },
        ],
      },
    ],
    docs: [
      {
        name: 'Business Docs',
        items: [
          {
            title: 'Goals & Vision',
            content: `Welcome to your business goals document!

Use this space to define your business vision and track your goals.

## Vision Statement
What is the big picture for your business? Write your vision here.

## Q1 Goals
- Goal 1: [Your first quarterly goal]
- Goal 2: [Your second quarterly goal]
- Goal 3: [Your third quarterly goal]

## Key Metrics
Track the numbers that matter most to your business.`,
          },
          {
            title: 'Client Notes Template',
            content: `# Client Meeting Notes

## Client Name
[Client name here]

## Date
[Meeting date]

## Attendees
- You
- [Client contact]

## Discussion Points
1.
2.
3.

## Action Items
- [ ]
- [ ]

## Next Steps
[What happens next]`,
          },
        ],
      },
    ],
  },
  {
    id: 'dev-team',
    name: 'Development Team',
    description: 'For software teams with sprints, bugs, and feature tracking',
    icon: 'code',
    color: '#1D4ED8',
    suggestedName: 'Product Development',
    boards: [
      {
        name: 'Sprint Board',
        columns: [
          {
            title: 'Backlog',
            color: '#F1F5F9',
            tasks: [
              'Implement user authentication',
              'Add search functionality',
              'Create API documentation',
              'Set up CI/CD pipeline',
            ],
          },
          {
            title: 'To Do',
            color: '#DBEAFE',
            tasks: [
              'Design database schema',
              'Write unit tests for auth module',
            ],
          },
          {
            title: 'In Progress',
            color: '#FEF3C7',
            tasks: ['Build REST API endpoints'],
          },
          {
            title: 'Review',
            color: '#EDE9FE',
            tasks: ['Code review: user profile feature'],
          },
          {
            title: 'Done',
            color: '#D1FAE5',
            tasks: ['Project setup and configuration'],
          },
        ],
      },
      {
        name: 'Bug Tracker',
        columns: [
          {
            title: 'Reported',
            color: '#FEE2E2',
            tasks: [
              'Login button not responding on mobile',
              'Date picker shows wrong format',
            ],
          },
          {
            title: 'Investigating',
            color: '#FEF3C7',
            tasks: ['Memory leak in dashboard component'],
          },
          {
            title: 'In Progress',
            color: '#DBEAFE',
            tasks: [],
          },
          {
            title: 'Fixed',
            color: '#D1FAE5',
            tasks: ['Fixed navigation menu collapse issue'],
          },
        ],
      },
    ],
    docs: [
      {
        name: 'Requirements',
        items: [
          {
            title: 'Product Requirements Document',
            content: `# Product Requirements Document (PRD)

## Overview
Brief description of the product and its purpose.

## Problem Statement
What problem are we solving? Who has this problem?

## Goals & Success Metrics
- **Goal 1**: [Description] - Measured by [metric]
- **Goal 2**: [Description] - Measured by [metric]

## User Stories
### As a [user type], I want to [action] so that [benefit]
- Acceptance criteria 1
- Acceptance criteria 2

## Features
### Feature 1: [Name]
**Priority**: High/Medium/Low
**Description**: What does this feature do?
**Requirements**:
- Requirement 1
- Requirement 2

### Feature 2: [Name]
**Priority**: High/Medium/Low
**Description**: What does this feature do?

## Out of Scope
What are we NOT building in this version?

## Timeline
| Phase | Duration | Deliverables |
|-------|----------|--------------|
| Design | 2 weeks | Wireframes, mockups |
| Development | 4 weeks | Core features |
| Testing | 1 week | QA complete |

## Open Questions
- Question 1?
- Question 2?`,
          },
          {
            title: 'Technical Specifications',
            content: `# Technical Specifications

## Architecture Overview
Describe the high-level system architecture.

## Tech Stack
- **Frontend**:
- **Backend**:
- **Database**:
- **Infrastructure**:

## API Design
### Endpoints
| Method | Endpoint | Description |
|--------|----------|-------------|
| GET | /api/users | Get all users |
| POST | /api/users | Create user |

## Database Schema
Describe the main tables/collections and their relationships.

## Security Considerations
- Authentication method
- Authorization rules
- Data encryption

## Performance Requirements
- Response time targets
- Scalability needs

## Dependencies
List external services and libraries.`,
          },
        ],
      },
      {
        name: 'Meeting Notes',
        items: [
          {
            title: 'Sprint Planning Template',
            content: `# Sprint Planning

## Sprint Number
Sprint [X]

## Sprint Goal
What is the main objective for this sprint?

## Sprint Duration
[Start Date] - [End Date]

## Team Capacity
- Developer 1: X hours
- Developer 2: X hours

## Selected Stories
| Story | Points | Assignee |
|-------|--------|----------|
| Story 1 | 3 | Dev 1 |
| Story 2 | 5 | Dev 2 |

## Total Points
[X] points committed

## Risks & Dependencies
- Risk 1
- Dependency 1

## Notes
Additional discussion points from planning.`,
          },
        ],
      },
    ],
  },
  {
    id: 'marketing',
    name: 'Marketing & Sales',
    description: 'Track campaigns, leads, and content creation',
    icon: 'campaign',
    color: '#059669',
    suggestedName: 'Marketing Campaigns',
    boards: [
      {
        name: 'Campaign Tracker',
        columns: [
          {
            title: 'Ideas',
            color: '#F1F5F9',
            tasks: [
              'Holiday promotion campaign',
              'Referral program launch',
              'Influencer partnership',
            ],
          },
          {
            title: 'Planning',
            color: '#DBEAFE',
            tasks: [
              'Q2 product launch campaign',
              'Email nurture sequence',
            ],
          },
          {
            title: 'In Progress',
            color: '#FEF3C7',
            tasks: ['Social media ad campaign'],
          },
          {
            title: 'Live',
            color: '#D1FAE5',
            tasks: ['Newsletter signup promotion'],
          },
          {
            title: 'Completed',
            color: '#E2E8F0',
            tasks: ['Brand awareness campaign - Jan'],
          },
        ],
      },
      {
        name: 'Content Calendar',
        columns: [
          {
            title: 'Ideas',
            color: '#F1F5F9',
            tasks: [
              'How-to guide: Getting started',
              'Customer success story interview',
              'Industry trends roundup',
            ],
          },
          {
            title: 'Writing',
            color: '#DBEAFE',
            tasks: ['Blog: 10 Tips for Productivity'],
          },
          {
            title: 'Review',
            color: '#EDE9FE',
            tasks: ['Case study: Enterprise client'],
          },
          {
            title: 'Scheduled',
            color: '#FEF3C7',
            tasks: ['Weekly newsletter - Feb 15'],
          },
          {
            title: 'Published',
            color: '#D1FAE5',
            tasks: ['Product announcement blog post'],
          },
        ],
      },
    ],
    docs: [
      {
        name: 'Brand & Strategy',
        items: [
          {
            title: 'Brand Guidelines',
            content: `# Brand Guidelines

## Brand Voice
How we communicate with our audience.

### Tone
- Professional yet friendly
- Clear and concise
- Empowering and supportive

### Words We Use
- "You" instead of "users"
- "Simple" instead of "easy"
- Active voice preferred

### Words We Avoid
- Jargon without explanation
- Negative framing
- Overpromising language

## Visual Identity
### Colors
- Primary: [Color code]
- Secondary: [Color code]
- Accent: [Color code]

### Typography
- Headings: [Font name]
- Body: [Font name]

### Logo Usage
Guidelines for logo placement and sizing.

## Social Media Guidelines
How to represent the brand on social platforms.`,
          },
          {
            title: 'Campaign Brief Template',
            content: `# Campaign Brief

## Campaign Name
[Campaign name]

## Objective
What are we trying to achieve?

## Target Audience
Who are we trying to reach?
- Demographics
- Interests
- Pain points

## Key Messages
1. Primary message
2. Supporting message
3. Call to action

## Channels
- [ ] Email
- [ ] Social media
- [ ] Paid ads
- [ ] Content marketing

## Budget
| Item | Cost |
|------|------|
| Ads | $X |
| Creative | $X |
| **Total** | $X |

## Timeline
| Milestone | Date |
|-----------|------|
| Brief approved | |
| Creative ready | |
| Launch | |
| Review | |

## Success Metrics
- Metric 1: Target
- Metric 2: Target`,
          },
        ],
      },
    ],
  },
  {
    id: 'client-project',
    name: 'Client Project',
    description: 'Manage deliverables and communication for client work',
    icon: 'handshake',
    color: '#D97706',
    suggestedName: 'Client Name - Project',
    boards: [
      {
        name: 'Deliverables',
        columns: [
          {
            title: 'Scope',
            color: '#F1F5F9',
            tasks: [
              'Homepage design',
              'Mobile responsive layouts',
              'Contact form integration',
              'Analytics setup',
            ],
          },
          {
            title: 'In Progress',
            color: '#DBEAFE',
            tasks: ['About page design'],
          },
          {
            title: 'Client Review',
            color: '#FEF3C7',
            tasks: ['Logo concepts - Round 2'],
          },
          {
            title: 'Revisions',
            color: '#FEE2E2',
            tasks: ['Header layout adjustments'],
          },
          {
            title: 'Approved',
            color: '#D1FAE5',
            tasks: [
              'Brand color palette',
              'Typography selection',
            ],
          },
        ],
      },
    ],
    docs: [
      {
        name: 'Project Documentation',
        items: [
          {
            title: 'Project Brief',
            content: `# Project Brief

## Client Information
- **Client Name**: [Name]
- **Contact**: [Email/Phone]
- **Company**: [Company name]

## Project Overview
What is this project about?

## Objectives
What does the client want to achieve?
1. Objective 1
2. Objective 2
3. Objective 3

## Scope of Work
### Included
- Deliverable 1
- Deliverable 2
- Deliverable 3

### Not Included
- Item 1
- Item 2

## Timeline
| Phase | Duration | Dates |
|-------|----------|-------|
| Discovery | 1 week | |
| Design | 2 weeks | |
| Development | 3 weeks | |
| Review | 1 week | |

## Budget
**Total Project Cost**: $[Amount]

| Milestone | Amount | Due |
|-----------|--------|-----|
| Deposit | $X | On signing |
| Phase 1 | $X | [Date] |
| Final | $X | On completion |

## Approval Process
How will deliverables be reviewed and approved?`,
          },
          {
            title: 'Meeting Notes',
            content: `# Client Meeting Notes

## Meeting Date
[Date]

## Attendees
- [Your name]
- [Client name]

## Agenda
1. Review current progress
2. Discuss feedback
3. Plan next steps

## Discussion Summary
Key points discussed in the meeting.

## Client Feedback
What feedback did the client provide?

## Decisions Made
- Decision 1
- Decision 2

## Action Items
| Task | Owner | Due Date |
|------|-------|----------|
| Task 1 | You | [Date] |
| Task 2 | Client | [Date] |

## Next Meeting
[Date and time]`,
          },
        ],
      },
    ],
  },
];
